import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import moment, { Moment } from 'moment';
import 'moment-timezone';
import CalendarIcon from '@mui/icons-material/CalendarMonthOutlined';
import { getCommonInputProps } from '../BaseInputLayout';
import Datetime from 'react-datetime';
import { DateInputLayoutProps, DateInputLayoutType } from 'framework/form';
import { DATE_FORMAT, DEFAULT_TIMEZONE } from '../../../../../utils';
import { Box, FormControl, FormHelperText, InputLabel, Typography } from '@mui/material';
import BaseInputErrors from '../BaseInputErrors';
import InputTooltip from '../InputTooltip';
import { CustomTimePicker } from './CustomTimePicker';
import { formatDateValueWithCaret } from './dateInputHelpers';
import { reactDateTimeSx } from './reactDateTimeSx';

interface FloatingLabelDateInputLayoutProps extends DateInputLayoutProps {}

const getBorderColor = (hasError: boolean, focused: boolean) => {
  if (hasError) return 'error.main';
  if (focused) return 'primary.main';
  return 'common.commonInputBorder';
};

const getLabelColor = (hasError: boolean, focused: boolean) => {
  if (hasError) return 'error.main';
  if (focused) return 'primary.main';
  return 'text.secondary';
};

const MIN_SPACE_FOR_CALENDAR = 300;

const FloatingLabelDateInputLayout: DateInputLayoutType = ({
  disabled,
  showCalendar = true,
  toggleButton = null,
  minDate: propsMinDate,
  maxDate: propsMaxDate,
  validRangeInclusivity = '[]',
  closeOnSelect,
  onBlurCallback,
  onFocusCallback,
  onChangeCallback,
  timezone = DEFAULT_TIMEZONE,
  className = '',
  readOnly,
  availableDates,
  dateIcon,
  sx,
  ...otherProps
}: FloatingLabelDateInputLayoutProps) => {
  const rest = {
    label: '',
    errorMessages: [],
    isVisible: true,
    isRequired: false,
    helpText: null,
    tooltip: null,
    disabled: false,
    ...otherProps,
  };

  const {
    label,
    source,
    errorMessages = [],
    isVisible = true,
    isRequired = false,
    helpText = null,
    tooltip = null,
    labelHint,
    placeholder,
    errorMessageMapper,
    value,
    timeFormat,
    dateFormat,
    autoComplete = 'off',
  } = rest;

  const dateTimeRef = useRef<Datetime>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const caretSelectionRef = useRef<{ start: number; end: number } | null>(null);
  const isManualTypingRef = useRef(false);
  const [inputValue, setInputValue] = useState<string>(() => (value ? String(value) : ''));
  const [isFocused, setIsFocused] = useState(false);
  const [openTop, setOpenTop] = useState(false);
  const [openLeft, setOpenLeft] = useState(false);

  const commonInputProps = getCommonInputProps(rest, false);
  const hasError = !!errorMessages?.length;
  const hasValue = Boolean(inputValue);

  const labelText = typeof label === 'function' ? label() : label || placeholder;
  const requiredValue = typeof isRequired === 'function' ? isRequired({}) : !!isRequired;
  const shouldBeShrinked = hasValue || isFocused;
  const dateTimeFormat = `${dateFormat || DATE_FORMAT.INPUT_VALUE}${timeFormat ? ` ${timeFormat}` : ''}`;

  useEffect(() => {
    if (!isManualTypingRef.current) {
      setInputValue(value ? String(value) : '');
    }
  }, [value]);

  useLayoutEffect(() => {
    const selection = caretSelectionRef.current;
    caretSelectionRef.current = null;

    if (selection && document.activeElement === inputRef.current) {
      inputRef.current?.setSelectionRange(selection.start, selection.end);
    }
  }, [inputValue]);

  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      const container = containerRef.current;
      if (container && container.contains(e.target as Node)) {
        setIsFocused(true);
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      const container = containerRef.current;
      if (container && !container.contains(e.relatedTarget as Node)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  const renderLabel = useCallback(() => {
    if (!tooltip) {
      return (
        <>
          {labelText}
          {requiredValue && <span> *</span>}
        </>
      );
    }

    return (
      <>
        {labelText}
        {requiredValue && <span> *</span>}
        <InputTooltip
          message={tooltip}
          source={source}
          sx={{
            pointerEvents: 'all',
            width: '14px',
          }}
        />
      </>
    );
  }, [tooltip, labelText, source, requiredValue]);

  const minDate =
    propsMinDate && ((propsMinDate instanceof moment ? propsMinDate : moment(propsMinDate)) as moment.Moment);
  const maxDate =
    propsMaxDate && ((propsMaxDate instanceof moment ? propsMaxDate : moment(propsMaxDate)) as moment.Moment);

  /**
   * This method is responsible for disabling picker values
   * according to provided minDate and maxDate props
   * @param currentDate -- date in opened calendar
   * @returns {boolean} -- if date should be 'pickable' or not.
   */
  const validateCalendar = (currentDate: Date) => {
    const includeStart = validRangeInclusivity && validRangeInclusivity[0] === '[';
    const includeEnd = validRangeInclusivity && validRangeInclusivity[1] === ']';

    if (maxDate && minDate && maxDate.isValid() && minDate.isValid()) {
      return moment.tz(currentDate, timezone).isBetween(minDate, maxDate, 'day', validRangeInclusivity);
    }

    if (maxDate && maxDate.isValid()) {
      return includeEnd ? maxDate.isSameOrAfter(currentDate) : maxDate.isAfter(currentDate);
    }

    if (minDate && minDate.isValid()) {
      return includeStart ? minDate.isSameOrBefore(currentDate) : minDate.isBefore(currentDate);
    }

    if (Array.isArray(availableDates) && availableDates.length) {
      return availableDates.includes(moment(currentDate).format(DATE_FORMAT.INPUT_VALUE));
    }

    return true;
  };

  const handleFocus = () => {
    const rect = containerRef?.current?.getBoundingClientRect();
    if (rect) {
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const spaceRight = window.innerWidth - rect.right;
      const spaceLeft = rect.left;

      const canOpenUp = spaceAbove > MIN_SPACE_FOR_CALENDAR;
      const canOpenDown = spaceBelow > MIN_SPACE_FOR_CALENDAR;
      const canOpenRight = spaceRight > MIN_SPACE_FOR_CALENDAR;
      const canOpenLeft = spaceLeft > MIN_SPACE_FOR_CALENDAR;

      if (!canOpenDown && canOpenUp) {
        setOpenTop(true);
      } else {
        setOpenTop(false);
      }

      if (!canOpenRight && canOpenLeft) {
        setOpenLeft(true);
      } else {
        setOpenLeft(false);
      }
    }

    setIsFocused(true);
    if (onFocusCallback) {
      onFocusCallback();
    }
  };

  const handleBlur = () => {
    if (isManualTypingRef.current && inputValue.trim()) {
      const parsed = moment(inputValue, dateTimeFormat, true);
      if (parsed.isValid() && !validateCalendar(parsed.toDate())) {
        setInputValue('');
        onChangeCallback?.({
          target: { value: '' },
        } as unknown as React.ChangeEvent<HTMLInputElement>);
      }
    }

    setIsFocused(false);
    isManualTypingRef.current = false;
    if (onBlurCallback) {
      onBlurCallback();
    }
  };
  const handleBackToCalendar = useCallback(() => {
    if (dateTimeRef.current) {
      (dateTimeRef.current as unknown as { navigate?: (viewMode: string) => void }).navigate?.('days');
    }
  }, []);

  const handleChangeDate = useCallback(
    (newValue: string | Moment) => {
      if (onChangeCallback) {
        const nextValue =
          typeof newValue === 'string' ? newValue : moment(newValue).format(dateTimeFormat || DATE_FORMAT.INPUT_VALUE);

        isManualTypingRef.current = false;
        setInputValue(nextValue);
        onChangeCallback({ target: { value: nextValue } } as unknown as React.ChangeEvent<HTMLInputElement>);
      }
    },
    [onChangeCallback, dateTimeFormat],
  );

  const emitInputChange = (nextValue: string) => {
    onChangeCallback?.({
      target: { value: nextValue },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  const handleRenderedInputFocus = (
    event: React.FocusEvent<HTMLInputElement>,
    inputOnFocus: React.FocusEventHandler<HTMLInputElement> | undefined,
    openCalendar: Function,
  ) => {
    inputOnFocus?.(event);
    handleFocus();
    openCalendar();
  };

  const handleRenderedInputBlur = (
    event: React.FocusEvent<HTMLInputElement>,
    inputOnBlur: React.FocusEventHandler<HTMLInputElement> | undefined,
  ) => {
    inputOnBlur?.(event);
    handleBlur();
  };

  const handleRenderedInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    isManualTypingRef.current = true;
    const {
      value: nextValue,
      selectionStart,
      selectionEnd,
    } = formatDateValueWithCaret(
      event.target.value,
      { dateFormat, timeFormat },
      event.target.selectionStart,
      event.target.selectionEnd,
    );

    setInputValue(nextValue);
    emitInputChange(nextValue);
    caretSelectionRef.current = { start: selectionStart, end: selectionEnd };
  };

  const renderCustomTimeView = useCallback(
    (mode: string, renderDefault: Function): React.ReactElement => {
      if (mode === 'time') {
        const parsedFromInput = moment(inputValue, dateTimeFormat, true);
        const currentDate = parsedFromInput.isValid()
          ? parsedFromInput
          : value
            ? moment.isMoment(value)
              ? value
              : moment(value)
            : moment();

        return (
          <CustomTimePicker date={currentDate} setDate={handleChangeDate} onBackToCalendar={handleBackToCalendar} />
        );
      }
      return renderDefault() as React.ReactElement;
    },
    [value, inputValue, dateTimeFormat, handleChangeDate, handleBackToCalendar],
  );

  if (!isVisible) {
    return null;
  }

  return (
    <FormControl
      ref={containerRef}
      className={className || ''}
      fullWidth
      error={hasError}
      variant="outlined"
      sx={[{ position: 'relative', mb: 2, transition: 'padding 0.3s ease' }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...(!hasValue && typeof tooltip === 'string' ? { title: tooltip } : {})}
    >
      <Box
        position="relative"
        sx={[
          ...(Array.isArray(reactDateTimeSx) ? reactDateTimeSx : [reactDateTimeSx]),
          {
            '& .rdt': { width: '100%' },
            '& input.floating-date-input': {
              boxSizing: 'border-box',
              width: '100%',
              height: '38px',
              py: '0.5rem',
              pr: '0.75rem',
              pl: '0.75rem',
              border: 0,
              outline: 0,
              boxShadow: 'none',
            },
            '& input.floating-date-input.with-calendar': { pl: '35px' },
            '& input.floating-date-input:focus': { outline: 0, boxShadow: 'none' },
          },
        ]}
      >
        {/* MUI-style fieldset with notched outline */}
        <Box
          component="fieldset"
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: '-5px 0 0',
            m: 0,
            p: '0 8px',
            minWidth: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            textAlign: 'left',
            border: '1px solid',
            borderColor: getBorderColor(hasError, isFocused),
            borderRadius: '4px',
            zIndex: 1,
          }}
        >
          <Box
            component="legend"
            sx={{
              float: 'unset',
              display: 'block',
              width: 'auto',
              height: '11px',
              maxWidth: shouldBeShrinked && labelText ? '100%' : 0,
              p: 0,
              overflow: 'hidden',
              visibility: shouldBeShrinked ? 'visible' : 'hidden',
              whiteSpace: 'nowrap',
              fontSize: '0.75em',
              transition: shouldBeShrinked
                ? 'max-width 100ms cubic-bezier(0, 0, 0.2, 1) 50ms'
                : 'max-width 50ms cubic-bezier(0, 0, 0.2, 1)',
            }}
          >
            <Typography
              component="span"
              variant="inherit"
              sx={{ display: 'inline-block', px: '5px', opacity: 0, visibility: 'hidden' }}
            >
              {renderLabel()}
            </Typography>
          </Box>
        </Box>

        {labelText && (
          <InputLabel
            htmlFor={source}
            shrink={shouldBeShrinked}
            error={hasError}
            sx={{
              '&&': {
                position: 'absolute',
                top: 0,
                left: 0,
                p: 0,
                maxWidth: showCalendar ? 'calc(100% - 48px)' : 'calc(100% - 24px)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: getLabelColor(hasError, isFocused),
                backgroundColor: shouldBeShrinked ? 'background.paper' : 'transparent',
                padding: shouldBeShrinked ? '0 0.5' : 0,
                pointerEvents: 'none',
                transformOrigin: 'top left',
                transform: shouldBeShrinked
                  ? 'translate(12px, -6px) scale(0.75)'
                  : `translate(${showCalendar ? 35 : 12}px, 8px)`,
                zIndex: 1,
                transition:
                  'color 200ms cubic-bezier(0, 0, 0.2, 1), transform 200ms cubic-bezier(0, 0, 0.2, 1), max-width 200ms cubic-bezier(0, 0, 0.2, 1)',
              },
            }}
          >
            {renderLabel()}
          </InputLabel>
        )}

        <Box>
          <Box
            className={classnames('datetime-container floating-date-wrapper', {
              'has-error': hasError,
              'open-top': openTop,
              'open-left': openLeft,
            })}
            role="button"
            position="relative"
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
            }}
          >
            {showCalendar
              ? dateIcon || (
                  <CalendarIcon
                    sx={{
                      position: 'absolute',
                      top: '9px',
                      left: '10px',
                      zIndex: 1,
                      mr: '10px',
                      fontSize: '1.2rem',
                      opacity: 0.8,
                    }}
                  />
                )
              : null}

            <Datetime
              {...commonInputProps}
              closeOnSelect={closeOnSelect ?? !timeFormat}
              ref={dateTimeRef}
              isValidDate={validateCalendar}
              onClose={handleBlur}
              onOpen={handleFocus}
              inputProps={{
                disabled: Boolean(disabled),
                readOnly,
              }}
              renderInput={(props: React.InputHTMLAttributes<HTMLInputElement>, openCalendar: Function) => {
                return (
                  <input
                    {...props}
                    id={source}
                    name={source}
                    autoComplete={autoComplete}
                    type="text"
                    ref={inputRef}
                    value={inputValue}
                    disabled={Boolean(disabled)}
                    readOnly={readOnly}
                    className={classnames('floating-date-input', props.className, { 'with-calendar': showCalendar })}
                    onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
                      handleRenderedInputFocus(e, props.onFocus, openCalendar);
                    }}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      handleRenderedInputBlur(e, props.onBlur);
                    }}
                    onChange={handleRenderedInputChange}
                  />
                );
              }}
              onChange={handleChangeDate}
              className={classnames(className, commonInputProps.className)}
              {...rest}
              value={inputValue}
              timeFormat={timeFormat || ''}
              renderView={renderCustomTimeView}
            />
          </Box>
        </Box>
      </Box>
      {labelHint && (
        <FormHelperText
          className="floating-label-date-input__label-hint"
          sx={{
            position: 'absolute',
            bottom: '-20px',
            left: '14px',
            fontSize: '0.75rem',
            color: 'text.secondary',
            backgroundColor: 'background.paper',
            padding: '0 4px',
            zIndex: 1,
            transition: 'all 0.2s ease-out',
            margin: 0,
          }}
        >
          {labelHint}
        </FormHelperText>
      )}
      {(hasError || helpText) && (
        <FormHelperText
          error={hasError}
          sx={{
            mt: 0.5,
            position: 'relative',
            '&.Mui-error': {
              left: 0,
              zIndex: 1,
              display: 'inline-block',
              lineHeight: 1.2,
              marginBottom: 0,
            },
            '& .form-error': {
              display: 'inline-block',
              transition: 'transform 0.3s ease',
              position: 'relative',
              fontSize: '0.7rem',
              lineHeight: 1.2,
              pt: 0.5,
            },
          }}
        >
          {hasError ? (
            <BaseInputErrors errorMessages={errorMessages} errorMessageMapper={errorMessageMapper} />
          ) : (
            helpText
          )}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default FloatingLabelDateInputLayout;
